import type { ReactNode } from 'react'
import {
  type Address,
  type InterestAssetMatchScore,
  type LeadListItemData,
  type Ownership,
} from '@aikoa/common'
import { useCurrentLanguage, useTranslations } from '@aikoa/i18n/react'
import { uniq } from 'lodash-es'
import { useMemo } from 'react'
import { Pressable, View } from 'react-native'
import Animated from 'react-native-reanimated'
import type { Lead, LeadTeaser } from '~/components/leads/types'
import type { DotVariant } from '~/components/ui/dot'
import { PropertyStats, type PropertyStat } from '~/components/assets/PropertyStats'
import {
  createHighlightItemsFromAssets,
  FeedHighlightItems,
  type MatchingAssetHighlight,
} from '~/components/feed/FeedHighlights'
import Icon from '~/components/Icon'
import { InterestMatchBadge } from '~/components/interests/InterestMatchBadge'
import { formatPriceValue, formatRange, formatRoomsRange } from '~/components/interests/utils'
import Image from '~/components/layout/Image'
import { LeadVerificationBadge, PurchasedBadge } from '~/components/leads/LeadBadges'
import { getLeadName } from '~/components/leads/utils'
import { Detail } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import { ButtonLabel, FakeButton } from '~/components/ui/button'
import { Dot } from '~/components/ui/dot'
import { Text } from '~/components/ui/text'
import { LeadTypeBadge } from '~/components/ui/TypeBadge'
import { getBuildingTypeLabel } from '~/lib/assets'
import { getRandomBackgroundImage } from '~/lib/themeImages'
import { cn } from '~/lib/utils'

export type TopMatchingAssetDisplay = {
  assetId: string
  assetName: string | null
  assetMedia: Array<{ url: string }> | null
  address: Address | null
  relationType: 'representation' | 'prospect'
  matchScores: InterestAssetMatchScore
}

const AnimatedPressable = Animated.createAnimatedComponent(Pressable)

type LeadListItemProps = {
  lead: Lead | LeadTeaser | LeadListItemData
  onPress?: () => void
  className?: string
  matchingAssets?: MatchingAssetHighlight[]
  notificationType?: DotVariant
  showTypeBadge?: boolean
}

export function LeadListItemWrapper({
  onPress,
  className,
  children,
}: {
  onPress?: () => void
  className?: string
  children: ReactNode
}) {
  return (
    <AnimatedPressable
      onPress={onPress}
      className={cn(
        'group/button native:shadow-card mx-2 my-1 rounded-xl bg-background shadow-md shadow-primary/30 transition-all duration-150 hover:bg-muted/50 active:scale-[0.97] active:bg-muted/50',
        className
      )}>
      {children}
    </AnimatedPressable>
  )
}

export function LeadImage({
  mediaUrl,
  className,
}: {
  mediaUrl: string | null
  className?: string
}) {
  const imageSource = useMemo(() => {
    if (!mediaUrl) {
      return getRandomBackgroundImage()
    }

    return { uri: mediaUrl }
  }, [mediaUrl])

  return (
    <View className={cn('relative shrink-0 overflow-hidden rounded-lg p-1.5 pr-0', className)}>
      <Image
        source={imageSource}
        contentFit="cover"
        cachePolicy="disk"
        transition={0}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: 7,
        }}
      />
    </View>
  )
}

function getLeadOwnership(lead: Lead | LeadTeaser | LeadListItemData): Ownership {
  if (lead.is_own) {
    return 'own'
  }

  if (lead.is_purchased) {
    // eslint-disable-next-line i18next/no-literal-string
    return 'purchased'
  }

  if (lead.organisation) {
    return 'organisation'
  }

  // eslint-disable-next-line i18next/no-literal-string
  return 'network'
}

export function LeadHeader({
  lead,
  notificationType,
  className,
  showTypeBadge = true,
}: {
  lead: Lead | LeadTeaser | LeadListItemData
  notificationType?: DotVariant
  className?: string
  showTypeBadge?: boolean
}) {
  const { t } = useTranslations('leads')

  const redactedName =
    !lead.is_own && !lead.is_purchased
      ? t(($) => $.verification.verifiedBuyer)
      : lead.is_verified
        ? t(($) => $.verification.verifiedContact)
        : t(($) => $.verification.newContact)

  const leadName = lead.name ? getLeadName(lead.name) || redactedName : redactedName

  const leadOwnership = getLeadOwnership(lead)

  const headerBadges = (
    <>
      {lead.is_verified ? (
        <LeadVerificationBadge label={false} />
      ) : (
        <Icon name="warning-outline" size={16} className="text-destructive" />
      )}
      {lead.is_purchased && <PurchasedBadge variant="compact" />}
    </>
  )

  const typeBadge = showTypeBadge ? (
    <LeadTypeBadge
      ownership={leadOwnership}
      organisationName={lead.organisation?.name}
      realtorName={lead.organisation?.realtorName}
      variant="inverted"
      badgeClassName="px-0"
    />
  ) : null

  return (
    <View className={cn('gap-x-2 gap-y-1 pr-2', className)}>
      <View className="flex-row items-center gap-2">
        <Heading2 className="font-medium text-lg/none" numberOfLines={1}>
          {leadName}
        </Heading2>
        {notificationType && <Dot variant={notificationType} />}
        {headerBadges}
      </View>
      {typeBadge}
    </View>
  )
}

function ShowButton() {
  const { t } = useTranslations('leads')

  return (
    <FakeButton variant="outline" size="xs" className="pointer-events-none flex-row gap-2">
      <ButtonLabel>{t(($) => $.list.showButton)}</ButtonLabel>
    </FakeButton>
  )
}

export function LeadListItem({
  lead,
  matchingAssets,
  onPress,
  className,
  notificationType,
  showTypeBadge = true,
}: LeadListItemProps) {
  const { t: tProperty } = useTranslations('property')
  const { t: tInterest } = useTranslations('interest')
  const { t: tLeads } = useTranslations('leads')
  const language = useCurrentLanguage()

  const locationsLabel = useMemo(() => {
    const locations = lead.interest.data?.locations ?? []

    if (locations.length === 0) {
      return ''
    }

    const labels = locations.map((location) => location.label ?? location.postalCode)

    return uniq(labels).join(', ')
  }, [lead.interest.data])

  const buildingTypesLabel = useMemo(() => {
    const buildingTypes = lead.interest.data?.filters?.buildingTypes ?? []

    if (buildingTypes.length === 0) {
      return ''
    }

    const labels = buildingTypes
      .map((buildingType) => getBuildingTypeLabel({ buildingType, t: tProperty, short: true }))
      .filter((label) => label.length > 0)

    if (labels.length === 0) {
      return ''
    }

    return uniq(labels).join(', ')
  }, [lead.interest.data, tProperty])

  const maxBudgetLabel = useMemo(() => {
    const maxBudget = lead.interest.data?.filters?.priceMax

    if (typeof maxBudget !== 'number') {
      return ''
    }

    return `${formatPriceValue(maxBudget, language)}`
  }, [lead.interest.data, language])

  const matchScores = 'matchScores' in lead ? lead.matchScores : null

  const topMatchingAssetsAsHighlights = useMemo((): MatchingAssetHighlight[] => {
    const topAssets = 'topMatchingAssets' in lead ? (lead.topMatchingAssets ?? []) : []

    return topAssets.map((asset) => ({
      assetId: asset.assetId,
      assetName: asset.assetName ?? '',
      address: asset.address,
      matchScores: asset.matchScores,
      isOwn: true,
      relationType: asset.relationType,
    }))
  }, [lead])

  const combinedMatchingAssets = useMemo(() => {
    if (matchingAssets && matchingAssets.length > 0) {
      return matchingAssets
    }

    return topMatchingAssetsAsHighlights
  }, [matchingAssets, topMatchingAssetsAsHighlights])

  const highlightItems = useMemo(
    () =>
      createHighlightItemsFromAssets({
        matchingAssets: combinedMatchingAssets,
        maxTypeItems: 2,
      }),
    [combinedMatchingAssets]
  )

  const showLeadMatchScore = !!matchScores

  const userMatchesLabel = tLeads(($) => $.list.userMatches)
  const bestMatchesLabel = tLeads(($) => $.list.bestMatches)

  const interestStats = useMemo(() => {
    const stats: PropertyStat[] = []

    if (locationsLabel) {
      stats.push({
        key: 'locations',
        icon: 'location-outline',
        value: locationsLabel,
      })
    }

    if (buildingTypesLabel) {
      stats.push({
        key: 'buildingTypes',
        icon: 'home-outline',
        value: buildingTypesLabel,
      })
    }

    const sizeMin = lead.interest.data?.filters?.sizeMin
    const sizeMax = lead.interest.data?.filters?.sizeMax
    const buildYearMin = lead.interest.data?.filters?.buildYearMin
    const roomsMin = lead.interest.data?.filters?.roomsMin
    const roomsMax = lead.interest.data?.filters?.roomsMax

    if (sizeMin || sizeMax) {
      const sizeLabel = formatRange(
        sizeMin ?? undefined,
        sizeMax ?? undefined,
        // eslint-disable-next-line i18next/no-literal-string
        'm²'
      )

      stats.push({
        key: 'size',
        icon: 'expand-outline',
        value: sizeLabel,
      })
    }

    if ((roomsMin || roomsMax) && highlightItems.length === 0) {
      const roomsLabel = formatRoomsRange(roomsMin ?? undefined, roomsMax ?? undefined, tInterest)

      stats.push({
        key: 'rooms',
        icon: 'grid-outline',
        value: roomsLabel,
      })
    }

    if (buildYearMin && highlightItems.length === 0) {
      stats.push({
        key: 'buildYear',
        icon: 'calendar-outline',
        value: buildYearMin,
      })
    }

    if (showLeadMatchScore && maxBudgetLabel) {
      stats.push({
        key: 'maxBudget',
        icon: 'less-than-or-equal',
        value: maxBudgetLabel,
      } satisfies PropertyStat)
    }

    return stats
  }, [
    buildingTypesLabel,
    lead.interest.data?.filters,
    highlightItems.length,
    locationsLabel,
    showLeadMatchScore,
    maxBudgetLabel,
    tInterest,
  ])

  const topMatchingAssetMedia =
    'topMatchingAssets' in lead ? lead.topMatchingAssets?.[0]?.assetMedia?.[0]?.url : null

  const mediaUrl = topMatchingAssetMedia ?? lead.media?.[0]?.url ?? null
  const hasHighlightItems = highlightItems.length > 0

  const footerContent = (
    <View className="flex-row items-center justify-between gap-2">
      <ShowButton />
      <View className={cn('ml-auto flex-row items-center gap-1', !hasHighlightItems && 'pr-3')}>
        {!!maxBudgetLabel && !showLeadMatchScore && (
          <View className="flex-row items-center gap-1">
            <Icon name="less-than-or-equal" size={14} className="text-foreground" />
            <Detail className="font-semibold text-base text-foreground">{maxBudgetLabel}</Detail>
          </View>
        )}
        {showLeadMatchScore && (
          <InterestMatchBadge
            scores={matchScores}
            className="items-end"
            breakdownClassName="items-end right-0"
          />
        )}
      </View>
    </View>
  )

  const mainContent = (
    <View className={cn('flex-1 gap-4', hasHighlightItems && 'py-3')}>
      <LeadHeader lead={lead} notificationType={notificationType} showTypeBadge={showTypeBadge} />

      {interestStats.length > 0 && (
        <PropertyStats stats={interestStats} variant="row" iconSize={14} />
      )}
    </View>
  )

  return (
    <LeadListItemWrapper
      onPress={onPress}
      className={cn('gap-1', !hasHighlightItems && 'flex-row gap-3', className)}>
      {!hasHighlightItems ? (
        <>
          <LeadImage mediaUrl={mediaUrl} className="w-[20%]" />
          <View className="flex-1 gap-3 py-3">
            {mainContent}
            {footerContent}
          </View>
        </>
      ) : (
        <>
          <View className="flex-row gap-3">
            <LeadImage mediaUrl={mediaUrl} className="w-[20%]" />
            {mainContent}
          </View>
          <View className="flex-1 gap-4 px-3 pb-3 pt-2">
            <View className="gap-2.5">
              <Text className="text-secondary/90! text-xxs uppercase">
                {matchingAssets && matchingAssets.length > 0 ? userMatchesLabel : bestMatchesLabel}
              </Text>
              <FeedHighlightItems
                items={highlightItems}
                className="-mx-3"
                maxItems={3}
                moreCount={
                  ('totalMatchingAssetsCount' in lead ? (lead.totalMatchingAssetsCount ?? 0) : 0) +
                  ('totalFeedbackResponsesCount' in lead
                    ? (lead.totalFeedbackResponsesCount ?? 0)
                    : 0) -
                  Math.min(highlightItems.length, 3)
                }
              />
            </View>
            {footerContent}
          </View>
        </>
      )}
    </LeadListItemWrapper>
  )
}
